<script setup lang="ts">
import ChartJs from "chart.js/auto";
import Chart from "@/base-components/Chart";
import {computed} from "vue";
import {useColorSchemeStore} from "@/stores/color-scheme";
import {getColor} from "@/utils/colors";
ChartJs.defaults.font.family = "Vazirmatn, Tahoma, 'Helvetica Neue', 'Helvetica', 'Arial', sans-serif"
const props = defineProps<{
  width?: number;
  height?: number;
  data: any;
  scaleMax?: number;
}>();
const colorScheme = computed(() => useColorSchemeStore().colorScheme);
const darkMode = false;
const formatter = new Intl.NumberFormat("en-US", {maximumFractionDigits:2});

function mapData(data: any) {
  const date = new Date(data.x)
  const usage =  data.y
  return {x: `${date.getHours().toString().padStart(2,'0')}:${date.getMinutes().toString().padStart(2, '0')}`, y: formatter.format(usage/1024/1024)}
}

function defaultLabels() {
  const startDate = new Date(Date.now() - 3_600_000)
  startDate.setSeconds(0)
  return new Array(12).fill(0).map((_, i) => {
    const date = new Date(startDate.getTime()+ (i* 300_000));
    return `${date.getHours().toString().padStart(2,'0')}:${date.getMinutes().toString().padStart(2, '0')}`
  })
}

const preparedData = computed(()=>({rx: props.data.rx.map(mapData), tx: props.data.tx.map(mapData)}))
const labels = computed(() => {
  const dataLabels = preparedData.value.rx.map((data: {x:string}) => data.x)
  return dataLabels.length >0 ?dataLabels : defaultLabels()
})


const data = computed(() => ({
  labels: labels.value,
  datasets: [{
    label: 'دریافت',
    borderWidth: 2,
    borderColor: colorScheme.value ? getColor("primary", 0.8) : "",
    backgroundColor: colorScheme.value ? getColor("primary", 0.8) : "",
    pointBorderColor: "transparent",
    tension: 0.4,
    data: preparedData.value.rx,
  },{
    label: 'ارسال',
    borderWidth: 2,
    borderColor: colorScheme.value ? getColor("danger", 0.8) : "",
    backgroundColor: colorScheme.value ? getColor("danger", 0.8) : "",
    pointBorderColor: "transparent",
    tension: 0.4,
    data: preparedData.value.tx,
  },
  ]
}))
const options = computed(() => {
  let scaleMaxY: string = `${props.scaleMax}`
  if (!scaleMaxY) {
    const maxRx = props.data.rx.reduce((acc, cur) => Math.max(acc, cur.y), 0)
    const maxTx = props.data.tx.reduce((acc, cur) => Math.max(acc, cur.y), 0)
    scaleMaxY = new Intl.NumberFormat('fa-IR', {maximumFractionDigits:2}).format(Math.min(10,Math.max(maxRx, maxTx)))
  }
  return {
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: true,
      },
        tooltip: {
          callbacks: {
            label: function(context) {
              let label = context.dataset.label || '';
              if (label) {
                label += ': ';
              }
              if (context.parsed.y !== null) {
                label += context.parsed.y + 'MB';
              }
              return label;
          }
        }
      }
    },
    responsive: true,
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
    scales: {
      x: {
        display: true,
        ticks: {
          autoSkip: true,
          maxTicksLimit: 20,
        }
      },
      y: {
        display: true,
        min: 0,
        max: scaleMaxY,
        ticks: {
          font: {
            size: 12,
          },
          color: getColor("slate.500", 0.8),
          callback: function (value) {
            return value + 'MB';
          },
        },
      },
    },
  };
});
</script>

<template>
  <Chart
      type="line"
      :width="props.width"
      :height="props.height"
      :data="data"
      :options="options"
  />
</template>

<style scoped>

</style>